'use client';

import Link from 'next/link';
import { ArrowLeft, Settings, Lock, KeyRound, Bell } from 'lucide-react';
import { Avatar, Button as DsButton } from '@/components/ds';
import { cn } from '@/lib/utils';

type Props = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  description?: string | null;
  memberCount: number;
  onlineCount?: number;
  privacy: 'public' | 'request' | 'private' | string;
  // Curated banner palette enum. Resolves through the shared --hdr-bg pattern
  // (data-banner sets --bn-deep/--bn-glow; theme-tokens.css builds --hdr-bg),
  // so it works in both Booth and Daylight with no per-theme branching.
  banner?: string | null;
  onOpenSettings?: () => void;
  // Manage button visibility — only owners/admins should see it.
  canManage?: boolean;
};

// Visibility as a real signal — three distinct treatments, not three labels.
function VisibilityChip({ privacy }: { privacy: Props['privacy'] }) {
  if (privacy === 'private') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-pill-active-bg text-pill-active-text">
        <Lock className="w-3 h-3" strokeWidth={2} />
        Private
      </span>
    );
  }
  if (privacy === 'request') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-status-away/15 text-status-away border border-status-away/30">
        <KeyRound className="w-3 h-3" strokeWidth={2} />
        Request to join
      </span>
    );
  }
  // Public — quietest treatment: open green dot + ghost chip.
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-surface-2 text-text-secondary border border-border-subtle">
      <span className="w-1.5 h-1.5 rounded-full bg-status-online" aria-hidden />
      Public
    </span>
  );
}

export default function GroupHeader({
  name,
  avatarUrl,
  description,
  memberCount,
  onlineCount,
  privacy,
  banner,
  onOpenSettings,
  canManage = true,
}: Props) {
  return (
    <header
      data-banner={banner || 'default'}
      className="relative px-4 sm:px-6 pt-4 pb-0 border-b border-border-subtle"
      style={{
        background: 'var(--hdr-bg)',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
      }}
    >
      <div className="relative flex flex-col gap-3">
        <Link
          href="/groups"
          className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary w-fit"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
          Back to Groups
        </Link>

        <div className="flex flex-wrap items-start gap-3">
          {/* Square, ringed, banner-tinted avatar tile. */}
          <div
            data-banner={banner || 'default'}
            className="rounded-md p-0.5"
            style={{ background: 'var(--hdr-bg)' }}
          >
            <Avatar src={avatarUrl || undefined} name={name} size={56} square ring />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-text-primary tracking-tight truncate">
                {name}
              </h1>
              <VisibilityChip privacy={privacy} />
            </div>
            {description && (
              <p className="text-xs text-text-secondary mt-1 max-w-[600px] line-clamp-2">
                {description}
              </p>
            )}
            <p className="text-[11px] font-mono text-text-tertiary mt-2">
              {memberCount} {memberCount === 1 ? 'member' : 'members'}
              {typeof onlineCount === 'number' && ` · ${onlineCount} online`}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            {/* Notify — stubbed; per-group notification prefs aren't built yet. */}
            <DsButton
              variant="ghost"
              size="sm"
              disabled
              title="Notifications coming soon"
              aria-label="Notifications (coming soon)"
            >
              <Bell className="w-3.5 h-3.5" strokeWidth={1.75} />
              Notify
            </DsButton>
            {canManage && (
              <DsButton variant="secondary" size="sm" onClick={() => onOpenSettings?.()}>
                <Settings className="w-3.5 h-3.5" strokeWidth={1.75} />
                Manage
              </DsButton>
            )}
          </div>
        </div>

        {/* Tabs row — Phase 1: Chat is the only content surface (no Reels/Files). */}
        <div className={cn('flex')}>
          <span className="pb-2 text-sm font-medium text-text-primary border-b-2 border-accent -mb-px">
            Chat
          </span>
        </div>
      </div>
    </header>
  );
}
